import React, { useMemo, useState } from 'react';
import { Col, Descriptions, Drawer, Row, Select, Space, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { formatMoney } from '@/utils/format';
import { cbsPathForCode } from '@/utils/cbsPathUtils';
import { epcWbsLibraryService } from '@/services/epcWbsLibrary.service';
import type { PackageRow } from './EpcWbsLibraryPackageCatalogue';

const { Text, Title } = Typography;

export interface EpcWbsLibraryPackageCompareDrawerProps {
  open: boolean;
  onClose: () => void;
  packages: PackageRow[];
  defaultLeftId?: string;
  defaultRightId?: string;
}

function CompareColumn({
  packageId,
  label,
}: {
  packageId?: string;
  label: string;
}) {
  const readinessQ = useQuery({
    queryKey: ['epc-wbs-lib-readiness', packageId],
    queryFn: () => epcWbsLibraryService.getPackageReadiness(packageId!),
    enabled: !!packageId,
  });

  const usageQ = useQuery({
    queryKey: ['epc-wbs-lib-usage', packageId],
    queryFn: () => epcWbsLibraryService.getPackageUsage(packageId!),
    enabled: !!packageId,
  });

  const templatesQ = useQuery({
    queryKey: ['epc-wbs-lib-cost-templates', packageId],
    queryFn: () => epcWbsLibraryService.listCostTemplates(packageId!),
    enabled: !!packageId,
  });

  const cbsQ = useQuery({
    queryKey: ['epc-cbs-codes'],
    queryFn: () => epcWbsLibraryService.listCbsCodes(),
  });

  const cbsNodes = useMemo(
    () =>
      (cbsQ.data || []).map((c: any) => ({
        id: String(c.id),
        code: String(c.code),
        name: c.name,
        parent_id: c.parent_id,
      })),
    [cbsQ.data]
  );

  const resourceTemplates = useMemo(
    () => (templatesQ.data || []).filter((t: any) => t.source_ref_label),
    [templatesQ.data]
  );

  if (!packageId) {
    return (
      <Col span={12}>
        <Text type="secondary">Select {label} package</Text>
      </Col>
    );
  }

  const r = readinessQ.data;
  const u = usageQ.data;

  return (
    <Col xs={24} md={12}>
      <Title level={5}>{label}</Title>
      {readinessQ.isLoading ? (
        <Text type="secondary">Loading…</Text>
      ) : r ? (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Tag color={r.isPlugReady ? 'success' : 'warning'}>
            {r.isPlugReady ? 'Plug-ready' : 'Needs work'}
          </Tag>
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="WBS nodes">{r.plugPreview.wbsNodeCount}</Descriptions.Item>
            <Descriptions.Item label="CWP packages">{r.plugPreview.packageLayerCount}</Descriptions.Item>
            <Descriptions.Item label="Cost lines">{r.plugPreview.costTemplateCount}</Descriptions.Item>
            <Descriptions.Item label="Default TEC">
              {formatMoney(r.rolledUpDefaultCost, { display: 'plain', maximumFractionDigits: 0 })}
            </Descriptions.Item>
            <Descriptions.Item label="Estimates using">{u?.estimateCount ?? '…'}</Descriptions.Item>
            <Descriptions.Item label="Revision plugs">{u?.revisionCount ?? '…'}</Descriptions.Item>
          </Descriptions>
          {r.cbsBreakdown.length > 0 ? (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                CBS breakdown
              </Text>
              <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                {r.cbsBreakdown.map((row) => (
                  <li key={row.cbsCode}>
                    <Tag>{cbsPathForCode(cbsNodes, row.cbsCode)}</Tag>{' '}
                    {formatMoney(row.amount, { display: 'plain', maximumFractionDigits: 0 })} (
                    {row.lineCount} lines)
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <Text type="secondary">No CBS templates</Text>
          )}
          {resourceTemplates.length > 0 ? (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Resource-class templates
              </Text>
              <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                {resourceTemplates.map((t: any) => (
                  <li key={t.id}>
                    <Tag>{t.source_ref_label}</Tag> {t.description}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </Space>
      ) : null}
    </Col>
  );
}

export const EpcWbsLibraryPackageCompareDrawer: React.FC<EpcWbsLibraryPackageCompareDrawerProps> = ({
  open,
  onClose,
  packages,
  defaultLeftId,
  defaultRightId,
}) => {
  const [leftId, setLeftId] = useState<string | undefined>(defaultLeftId);
  const [rightId, setRightId] = useState<string | undefined>(defaultRightId);

  const options = useMemo(
    () =>
      packages.map((p) => ({
        value: String(p.id),
        label: `${p.code} — ${p.name}`,
      })),
    [packages]
  );

  return (
    <Drawer title="Compare packages" open={open} onClose={onClose} width={720} destroyOnClose>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Row gutter={12}>
          <Col span={12}>
            <Text type="secondary">Package A</Text>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Select package"
              options={options}
              value={leftId}
              onChange={setLeftId}
            />
          </Col>
          <Col span={12}>
            <Text type="secondary">Package B</Text>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Select package"
              options={options}
              value={rightId}
              onChange={setRightId}
            />
          </Col>
        </Row>
        <Row gutter={24}>
          <CompareColumn packageId={leftId} label="A" />
          <CompareColumn packageId={rightId} label="B" />
        </Row>
      </Space>
    </Drawer>
  );
};
